import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header, Footer } from "@/components/Header";
import { api, mediaUrl } from "@/lib/api";
import { ArrowLeft, ExternalLink, Clock } from "lucide-react";

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const Attr = ({ label, value }) =>
  value ? (
    <div className="border-t border-white/10 py-4 flex justify-between items-center">
      <span className="text-xs font-mono uppercase tracking-[0.15em] text-[#8E8E9A]">{label}</span>
      <span className="font-serif text-lg font-light text-[#F8F6F0]">{value}</span>
    </div>
  ) : null;

export default function ProductDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err)
    return (
      <div className="min-h-screen bg-[#0B0B0C]">
        <Header />
        <div className="pt-40 text-center">
          <p className="overline mb-4">Product unavailable</p>
          <Link to="/products" className="text-[#D4AF37]">Return to Products</Link>
        </div>
      </div>
    );
  if (!data) return <div className="min-h-screen bg-[#0B0B0C]" />;

  const { product, related_looks } = data;

  return (
    <div className="min-h-screen bg-[#0B0B0C] relative z-[2]">
      <Header />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 pt-28 pb-24">
        <Link to="/products" className="inline-flex items-center gap-2 overline mb-8 hover:text-[#D4AF37]" data-testid="back-to-products">
          <ArrowLeft size={13} /> Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Media */}
          <div className="lg:col-span-6">
            <div className="lg:sticky lg:top-28 aspect-[4/5] overflow-hidden rounded-sm bg-[#141417] border border-white/10">
              <img src={mediaUrl(product.hero_thumbnail)} alt={product.title} className="w-full h-full object-cover" data-testid="product-hero-image" />
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-6">
            <p className="overline mb-3" data-testid="product-detail-brand">{product.brand || product.product_type}</p>
            <h1 className="font-serif text-4xl lg:text-6xl font-light leading-tight" data-testid="product-title">
              {product.title}
            </h1>

            {product.price && (
              <p className="font-mono text-lg text-[#D4AF37] mt-4" data-testid="product-detail-price">
                {Math.round(parseFloat(product.price))}
              </p>
            )}

            <div className="flex flex-wrap gap-2 mt-6 mb-8">
              {(product.style_labels || []).map((s) => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37]">
                  {s}
                </span>
              ))}
            </div>

            <a
              href={product.shop_url || "https://theluxbel.com"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shop-external-link-button"
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-[#C5A059] transition-colors duration-300 mb-10"
            >
              {product.brand ? `Shop at ${product.brand}` : "View on theluxbel.com"}
              <ExternalLink size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>

            <div className="mb-12">
              <Attr label="Color" value={product.color} />
              <Attr label="Fabric" value={product.fabric} />
              <Attr label="Occasion" value={product.occasion} />
              <Attr label="Type" value={product.product_type} />
            </div>

            {related_looks?.length > 0 && (
              <div>
                <p className="overline mb-5">Appears in {related_looks.length} look(s)</p>
                <div className="grid grid-cols-2 gap-4" data-testid="related-looks">
                  {related_looks.map(({ look, timestamp_start }) => (
                    <Link
                      key={look.id}
                      to={`/looks/${look.id}`}
                      data-testid={`related-look-${look.id}`}
                      className="group relative aspect-video overflow-hidden rounded-sm border border-white/10 hover:border-[#D4AF37]/40 transition-all"
                    >
                      <img src={mediaUrl(look.hero_thumbnail)} alt={look.title} className="absolute inset-0 w-full h-full object-cover img-zoom" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute bottom-0 inset-x-0 p-3">
                        <p className="font-serif text-sm text-[#F8F6F0] group-hover:text-[#D4AF37] transition-colors">{look.title}</p>
                        <span className="flex items-center gap-1 text-[10px] font-mono text-[#8E8E9A] mt-1">
                          <Clock size={9} /> {fmtTime(timestamp_start)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
